import { z } from 'zod';

export const themeModeSchema = z.enum(['dark', 'light']);
export type ThemeMode = z.infer<typeof themeModeSchema>;

export const appThemeIdSchema = z.enum([
	'default-dark',
	'default-light',
	'pastel-de-nata',
	'graphite-ledger',
	'ink-well',
	'moss-study',
	'oxblood-club',
	'fjord-dusk',
	'limestone',
	'paper-and-ink',
	'sage-receipt',
	'terracotta-desk',
	'harbor-mist',
]);
export type AppThemeId = z.infer<typeof appThemeIdSchema>;

export const themeVariablesSchema = z.object({
	'--background': z.string(),
	'--foreground': z.string(),
	'--card': z.string(),
	'--card-foreground': z.string(),
	'--popover': z.string(),
	'--popover-foreground': z.string(),
	'--primary': z.string(),
	'--primary-foreground': z.string(),
	'--secondary': z.string(),
	'--secondary-foreground': z.string(),
	'--muted': z.string(),
	'--muted-foreground': z.string(),
	'--accent': z.string(),
	'--accent-foreground': z.string(),
	'--destructive': z.string(),
	'--destructive-foreground': z.string(),
	'--border': z.string(),
	'--input': z.string(),
	'--ring': z.string(),
	'--chart-1': z.string(),
	'--chart-2': z.string(),
	'--chart-3': z.string(),
	'--chart-4': z.string(),
	'--chart-5': z.string(),
	'--sidebar-background': z.string(),
	'--sidebar-foreground': z.string(),
	'--sidebar-primary': z.string(),
	'--sidebar-primary-foreground': z.string(),
	'--sidebar-accent': z.string(),
	'--sidebar-accent-foreground': z.string(),
	'--sidebar-border': z.string(),
	'--sidebar-ring': z.string(),
});

export type ThemeVariables = z.infer<typeof themeVariablesSchema>;

export const appThemeSchema = z.object({
	id: appThemeIdSchema,
	name: z.string().min(1),
	description: z.string().min(1),
	mode: themeModeSchema,
	variables: themeVariablesSchema,
});

export type AppTheme = z.infer<typeof appThemeSchema>;

export const BASE_THEME_VARIABLES = z.record(z.string(), z.string()).parse({
	'--radius': '1.5rem',
	'--font-sans': "'Google Sans Flex'",
	'--font-mono': "'Google Sans Code'",
});

export const APP_THEMES = appThemeSchema.array().parse([
	{
		id: 'default-dark',
		name: 'Default Dark',
		description: 'Original dark theme',
		mode: 'dark',
		variables: {
			'--background': '240 10% 3.9%',
			'--foreground': '0 0% 98%',
			'--card': '240 10% 3.9%',
			'--card-foreground': '0 0% 98%',
			'--popover': '240 10% 3.9%',
			'--popover-foreground': '0 0% 98%',
			'--primary': '0 0% 98%',
			'--primary-foreground': '240 5.9% 10%',
			'--secondary': '240 3.7% 15.9%',
			'--secondary-foreground': '0 0% 98%',
			'--muted': '240 3.7% 15.9%',
			'--muted-foreground': '240 5% 64.9%',
			'--accent': '240 3.7% 15.9%',
			'--accent-foreground': '0 0% 98%',
			'--destructive': '0 62.8% 30.6%',
			'--destructive-foreground': '0 0% 98%',
			'--border': '240 3.7% 15.9%',
			'--input': '240 3.7% 15.9%',
			'--ring': '240 4.9% 83.9%',
			'--chart-1': '220 70% 50%',
			'--chart-2': '160 60% 45%',
			'--chart-3': '30 80% 55%',
			'--chart-4': '280 65% 60%',
			'--chart-5': '340 75% 55%',
			'--sidebar-background': '240 5.9% 10%',
			'--sidebar-foreground': '240 4.8% 95.9%',
			'--sidebar-primary': '224.3 76.3% 48%',
			'--sidebar-primary-foreground': '0 0% 100%',
			'--sidebar-accent': '240 3.7% 15.9%',
			'--sidebar-accent-foreground': '240 4.8% 95.9%',
			'--sidebar-border': '240 3.7% 15.9%',
			'--sidebar-ring': '217.2 91.2% 59.8%',
		},
	},
	{
		id: 'default-light',
		name: 'Default Light',
		description: 'Neutral light theme with softer contrast',
		mode: 'light',
		variables: {
			'--background': '220 14% 90%',
			'--foreground': '222 28% 16%',
			'--card': '220 20% 95%',
			'--card-foreground': '222 28% 16%',
			'--popover': '220 20% 95%',
			'--popover-foreground': '222 28% 16%',
			'--primary': '221 36% 25%',
			'--primary-foreground': '210 20% 98%',
			'--secondary': '220 13% 83%',
			'--secondary-foreground': '222 28% 16%',
			'--muted': '220 12% 81%',
			'--muted-foreground': '222 12% 37%',
			'--accent': '211 32% 78%',
			'--accent-foreground': '222 28% 16%',
			'--destructive': '0 72% 50%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '220 11% 70%',
			'--input': '220 11% 70%',
			'--ring': '221 36% 25%',
			'--chart-1': '221 36% 25%',
			'--chart-2': '193 42% 38%',
			'--chart-3': '27 62% 48%',
			'--chart-4': '148 30% 36%',
			'--chart-5': '336 45% 44%',
			'--sidebar-background': '220 13% 87%',
			'--sidebar-foreground': '222 28% 16%',
			'--sidebar-primary': '221 36% 25%',
			'--sidebar-primary-foreground': '210 20% 98%',
			'--sidebar-accent': '211 32% 78%',
			'--sidebar-accent-foreground': '222 28% 16%',
			'--sidebar-border': '220 11% 70%',
			'--sidebar-ring': '221 36% 25%',
		},
	},
	{
		id: 'pastel-de-nata',
		name: 'Pastel de Nata',
		description: 'Warm pastry-toned light theme',
		mode: 'light',
		variables: {
			'--background': '37 34% 90%',
			'--foreground': '18 26% 20%',
			'--card': '40 40% 95%',
			'--card-foreground': '18 26% 20%',
			'--popover': '40 40% 95%',
			'--popover-foreground': '18 26% 20%',
			'--primary': '24 56% 32%',
			'--primary-foreground': '38 60% 97%',
			'--secondary': '34 44% 82%',
			'--secondary-foreground': '18 26% 20%',
			'--muted': '36 28% 80%',
			'--muted-foreground': '20 14% 36%',
			'--accent': '24 62% 76%',
			'--accent-foreground': '18 26% 20%',
			'--destructive': '0 72% 50%',
			'--destructive-foreground': '0 0% 98%',
			'--border': '30 25% 70%',
			'--input': '30 25% 70%',
			'--ring': '24 56% 32%',
			'--chart-1': '24 56% 32%',
			'--chart-2': '43 72% 48%',
			'--chart-3': '15 61% 55%',
			'--chart-4': '145 29% 39%',
			'--chart-5': '338 44% 48%',
			'--sidebar-background': '35 32% 86%',
			'--sidebar-foreground': '18 26% 20%',
			'--sidebar-primary': '24 56% 32%',
			'--sidebar-primary-foreground': '38 60% 97%',
			'--sidebar-accent': '24 62% 76%',
			'--sidebar-accent-foreground': '18 26% 20%',
			'--sidebar-border': '30 25% 70%',
			'--sidebar-ring': '24 56% 32%',
		},
	},
	// editorial dark themes
	{
		id: 'graphite-ledger',
		name: 'Graphite Ledger',
		description: 'Neutral charcoal with steel-blue accents',
		mode: 'dark',
		variables: {
			'--background': '220 10% 9%',
			'--foreground': '210 12% 93%',
			'--card': '220 10% 12%',
			'--card-foreground': '210 12% 93%',
			'--popover': '220 10% 12%',
			'--popover-foreground': '210 12% 93%',
			'--primary': '210 34% 72%',
			'--primary-foreground': '220 18% 12%',
			'--secondary': '220 9% 18%',
			'--secondary-foreground': '210 12% 93%',
			'--muted': '220 8% 18%',
			'--muted-foreground': '215 10% 70%',
			'--accent': '214 14% 24%',
			'--accent-foreground': '210 12% 93%',
			'--destructive': '0 68% 56%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '220 8% 24%',
			'--input': '220 8% 24%',
			'--ring': '210 34% 72%',
			'--chart-1': '210 34% 72%',
			'--chart-2': '186 28% 58%',
			'--chart-3': '32 56% 62%',
			'--chart-4': '271 26% 66%',
			'--chart-5': '350 45% 63%',
			'--sidebar-background': '220 12% 11%',
			'--sidebar-foreground': '210 12% 93%',
			'--sidebar-primary': '210 34% 72%',
			'--sidebar-primary-foreground': '220 18% 12%',
			'--sidebar-accent': '214 14% 24%',
			'--sidebar-accent-foreground': '210 12% 93%',
			'--sidebar-border': '220 8% 24%',
			'--sidebar-ring': '210 34% 72%',
		},
	},
	{
		id: 'ink-well',
		name: 'Ink Well',
		description: 'Warm near-black with navy depth and parchment text',
		mode: 'dark',
		variables: {
			'--background': '228 18% 9%',
			'--foreground': '40 22% 91%',
			'--card': '229 18% 12%',
			'--card-foreground': '40 22% 91%',
			'--popover': '229 18% 12%',
			'--popover-foreground': '40 22% 91%',
			'--primary': '210 40% 72%',
			'--primary-foreground': '228 24% 12%',
			'--secondary': '229 13% 18%',
			'--secondary-foreground': '40 22% 91%',
			'--muted': '229 12% 18%',
			'--muted-foreground': '38 12% 70%',
			'--accent': '222 18% 23%',
			'--accent-foreground': '40 22% 91%',
			'--destructive': '0 68% 56%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '228 12% 26%',
			'--input': '228 12% 26%',
			'--ring': '210 40% 72%',
			'--chart-1': '210 40% 72%',
			'--chart-2': '40 38% 68%',
			'--chart-3': '173 24% 56%',
			'--chart-4': '286 30% 67%',
			'--chart-5': '356 50% 63%',
			'--sidebar-background': '229 20% 11%',
			'--sidebar-foreground': '40 22% 91%',
			'--sidebar-primary': '210 40% 72%',
			'--sidebar-primary-foreground': '228 24% 12%',
			'--sidebar-accent': '222 18% 23%',
			'--sidebar-accent-foreground': '40 22% 91%',
			'--sidebar-border': '228 12% 26%',
			'--sidebar-ring': '210 40% 72%',
		},
	},
	{
		id: 'moss-study',
		name: 'Moss Study',
		description: 'Forest-charcoal with muted olive accents',
		mode: 'dark',
		variables: {
			'--background': '140 14% 10%',
			'--foreground': '60 16% 90%',
			'--card': '141 14% 13%',
			'--card-foreground': '60 16% 90%',
			'--popover': '141 14% 13%',
			'--popover-foreground': '60 16% 90%',
			'--primary': '92 25% 64%',
			'--primary-foreground': '142 18% 12%',
			'--secondary': '142 12% 18%',
			'--secondary-foreground': '60 16% 90%',
			'--muted': '142 10% 18%',
			'--muted-foreground': '76 10% 69%',
			'--accent': '138 16% 24%',
			'--accent-foreground': '60 16% 90%',
			'--destructive': '0 63% 55%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '140 10% 26%',
			'--input': '140 10% 26%',
			'--ring': '92 25% 64%',
			'--chart-1': '92 25% 64%',
			'--chart-2': '148 24% 48%',
			'--chart-3': '36 48% 62%',
			'--chart-4': '206 30% 63%',
			'--chart-5': '338 30% 62%',
			'--sidebar-background': '140 15% 11%',
			'--sidebar-foreground': '60 16% 90%',
			'--sidebar-primary': '92 25% 64%',
			'--sidebar-primary-foreground': '142 18% 12%',
			'--sidebar-accent': '138 16% 24%',
			'--sidebar-accent-foreground': '60 16% 90%',
			'--sidebar-border': '140 10% 26%',
			'--sidebar-ring': '92 25% 64%',
		},
	},
	{
		id: 'oxblood-club',
		name: 'Oxblood Club',
		description: 'Wine-black surfaces with brass-toned highlights',
		mode: 'dark',
		variables: {
			'--background': '348 18% 10%',
			'--foreground': '34 28% 90%',
			'--card': '348 18% 13%',
			'--card-foreground': '34 28% 90%',
			'--popover': '348 18% 13%',
			'--popover-foreground': '34 28% 90%',
			'--primary': '38 42% 68%',
			'--primary-foreground': '348 24% 13%',
			'--secondary': '348 12% 19%',
			'--secondary-foreground': '34 28% 90%',
			'--muted': '348 10% 19%',
			'--muted-foreground': '30 12% 70%',
			'--accent': '356 20% 24%',
			'--accent-foreground': '34 28% 90%',
			'--destructive': '0 65% 56%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '349 12% 27%',
			'--input': '349 12% 27%',
			'--ring': '38 42% 68%',
			'--chart-1': '38 42% 68%',
			'--chart-2': '350 36% 62%',
			'--chart-3': '200 28% 60%',
			'--chart-4': '275 26% 66%',
			'--chart-5': '122 18% 56%',
			'--sidebar-background': '348 20% 11%',
			'--sidebar-foreground': '34 28% 90%',
			'--sidebar-primary': '38 42% 68%',
			'--sidebar-primary-foreground': '348 24% 13%',
			'--sidebar-accent': '356 20% 24%',
			'--sidebar-accent-foreground': '34 28% 90%',
			'--sidebar-border': '349 12% 27%',
			'--sidebar-ring': '38 42% 68%',
		},
	},
	{
		id: 'fjord-dusk',
		name: 'Fjord Dusk',
		description: 'Blue-slate evening palette with restrained cool contrast',
		mode: 'dark',
		variables: {
			'--background': '210 18% 11%',
			'--foreground': '205 20% 92%',
			'--card': '211 18% 14%',
			'--card-foreground': '205 20% 92%',
			'--popover': '211 18% 14%',
			'--popover-foreground': '205 20% 92%',
			'--primary': '198 38% 72%',
			'--primary-foreground': '212 24% 14%',
			'--secondary': '212 14% 20%',
			'--secondary-foreground': '205 20% 92%',
			'--muted': '212 12% 20%',
			'--muted-foreground': '205 12% 70%',
			'--accent': '216 18% 25%',
			'--accent-foreground': '205 20% 92%',
			'--destructive': '0 66% 56%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '212 12% 28%',
			'--input': '212 12% 28%',
			'--ring': '198 38% 72%',
			'--chart-1': '198 38% 72%',
			'--chart-2': '216 30% 62%',
			'--chart-3': '36 52% 63%',
			'--chart-4': '166 28% 55%',
			'--chart-5': '282 28% 68%',
			'--sidebar-background': '211 20% 12%',
			'--sidebar-foreground': '205 20% 92%',
			'--sidebar-primary': '198 38% 72%',
			'--sidebar-primary-foreground': '212 24% 14%',
			'--sidebar-accent': '216 18% 25%',
			'--sidebar-accent-foreground': '205 20% 92%',
			'--sidebar-border': '212 12% 28%',
			'--sidebar-ring': '198 38% 72%',
		},
	},
	// editorial light themes
	{
		id: 'limestone',
		name: 'Limestone',
		description: 'Calm mineral grays with steady contrast',
		mode: 'light',
		variables: {
			'--background': '42 16% 92%',
			'--foreground': '222 16% 18%',
			'--card': '40 18% 96%',
			'--card-foreground': '222 16% 18%',
			'--popover': '40 18% 96%',
			'--popover-foreground': '222 16% 18%',
			'--primary': '220 18% 28%',
			'--primary-foreground': '42 30% 97%',
			'--secondary': '38 10% 84%',
			'--secondary-foreground': '222 16% 18%',
			'--muted': '40 9% 82%',
			'--muted-foreground': '220 10% 38%',
			'--accent': '210 16% 80%',
			'--accent-foreground': '222 16% 18%',
			'--destructive': '0 72% 48%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '34 8% 69%',
			'--input': '34 8% 69%',
			'--ring': '220 18% 28%',
			'--chart-1': '220 18% 28%',
			'--chart-2': '188 34% 38%',
			'--chart-3': '30 44% 48%',
			'--chart-4': '142 20% 38%',
			'--chart-5': '336 30% 44%',
			'--sidebar-background': '42 14% 88%',
			'--sidebar-foreground': '222 16% 18%',
			'--sidebar-primary': '220 18% 28%',
			'--sidebar-primary-foreground': '42 30% 97%',
			'--sidebar-accent': '210 16% 80%',
			'--sidebar-accent-foreground': '222 16% 18%',
			'--sidebar-border': '34 8% 69%',
			'--sidebar-ring': '220 18% 28%',
		},
	},
	{
		id: 'paper-and-ink',
		name: 'Paper and Ink',
		description: 'Editorial off-white paper with dark ink text',
		mode: 'light',
		variables: {
			'--background': '44 24% 94%',
			'--foreground': '220 22% 13%',
			'--card': '44 26% 98%',
			'--card-foreground': '220 22% 13%',
			'--popover': '44 26% 98%',
			'--popover-foreground': '220 22% 13%',
			'--primary': '220 24% 20%',
			'--primary-foreground': '44 30% 97%',
			'--secondary': '42 16% 86%',
			'--secondary-foreground': '220 22% 13%',
			'--muted': '42 12% 84%',
			'--muted-foreground': '220 12% 35%',
			'--accent': '210 24% 80%',
			'--accent-foreground': '220 22% 13%',
			'--destructive': '0 68% 48%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '38 10% 71%',
			'--input': '38 10% 71%',
			'--ring': '220 24% 20%',
			'--chart-1': '220 24% 20%',
			'--chart-2': '28 48% 44%',
			'--chart-3': '196 34% 40%',
			'--chart-4': '144 24% 38%',
			'--chart-5': '336 32% 46%',
			'--sidebar-background': '44 18% 90%',
			'--sidebar-foreground': '220 22% 13%',
			'--sidebar-primary': '220 24% 20%',
			'--sidebar-primary-foreground': '44 30% 97%',
			'--sidebar-accent': '210 24% 80%',
			'--sidebar-accent-foreground': '220 22% 13%',
			'--sidebar-border': '38 10% 71%',
			'--sidebar-ring': '220 24% 20%',
		},
	},
	{
		id: 'sage-receipt',
		name: 'Sage Receipt',
		description: 'Dusty sage paper tones that stay easy on the eyes',
		mode: 'light',
		variables: {
			'--background': '108 16% 92%',
			'--foreground': '156 18% 18%',
			'--card': '106 18% 96%',
			'--card-foreground': '156 18% 18%',
			'--popover': '106 18% 96%',
			'--popover-foreground': '156 18% 18%',
			'--primary': '154 24% 30%',
			'--primary-foreground': '60 18% 97%',
			'--secondary': '102 12% 85%',
			'--secondary-foreground': '156 18% 18%',
			'--muted': '106 10% 83%',
			'--muted-foreground': '156 10% 36%',
			'--accent': '112 18% 79%',
			'--accent-foreground': '156 18% 18%',
			'--destructive': '0 69% 48%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '112 8% 68%',
			'--input': '112 8% 68%',
			'--ring': '154 24% 30%',
			'--chart-1': '154 24% 30%',
			'--chart-2': '86 28% 40%',
			'--chart-3': '28 52% 48%',
			'--chart-4': '206 28% 44%',
			'--chart-5': '336 30% 44%',
			'--sidebar-background': '108 14% 88%',
			'--sidebar-foreground': '156 18% 18%',
			'--sidebar-primary': '154 24% 30%',
			'--sidebar-primary-foreground': '60 18% 97%',
			'--sidebar-accent': '112 18% 79%',
			'--sidebar-accent-foreground': '156 18% 18%',
			'--sidebar-border': '112 8% 68%',
			'--sidebar-ring': '154 24% 30%',
		},
	},
	{
		id: 'terracotta-desk',
		name: 'Terracotta Desk',
		description: 'Warm clay-beige surfaces with grounded auburn accents',
		mode: 'light',
		variables: {
			'--background': '28 24% 92%',
			'--foreground': '18 18% 18%',
			'--card': '30 28% 96%',
			'--card-foreground': '18 18% 18%',
			'--popover': '30 28% 96%',
			'--popover-foreground': '18 18% 18%',
			'--primary': '16 40% 34%',
			'--primary-foreground': '34 40% 97%',
			'--secondary': '28 18% 84%',
			'--secondary-foreground': '18 18% 18%',
			'--muted': '28 14% 82%',
			'--muted-foreground': '18 10% 36%',
			'--accent': '20 34% 77%',
			'--accent-foreground': '18 18% 18%',
			'--destructive': '0 70% 48%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '24 12% 68%',
			'--input': '24 12% 68%',
			'--ring': '16 40% 34%',
			'--chart-1': '16 40% 34%',
			'--chart-2': '36 58% 46%',
			'--chart-3': '196 30% 42%',
			'--chart-4': '136 24% 38%',
			'--chart-5': '330 30% 45%',
			'--sidebar-background': '28 20% 88%',
			'--sidebar-foreground': '18 18% 18%',
			'--sidebar-primary': '16 40% 34%',
			'--sidebar-primary-foreground': '34 40% 97%',
			'--sidebar-accent': '20 34% 77%',
			'--sidebar-accent-foreground': '18 18% 18%',
			'--sidebar-border': '24 12% 68%',
			'--sidebar-ring': '16 40% 34%',
		},
	},
	{
		id: 'harbor-mist',
		name: 'Harbor Mist',
		description: 'Foggy blue-gray light theme with cooler air',
		mode: 'light',
		variables: {
			'--background': '205 24% 92%',
			'--foreground': '214 22% 18%',
			'--card': '204 28% 96%',
			'--card-foreground': '214 22% 18%',
			'--popover': '204 28% 96%',
			'--popover-foreground': '214 22% 18%',
			'--primary': '211 30% 32%',
			'--primary-foreground': '210 28% 97%',
			'--secondary': '205 18% 84%',
			'--secondary-foreground': '214 22% 18%',
			'--muted': '204 14% 82%',
			'--muted-foreground': '214 10% 38%',
			'--accent': '193 28% 79%',
			'--accent-foreground': '214 22% 18%',
			'--destructive': '0 70% 48%',
			'--destructive-foreground': '0 0% 100%',
			'--border': '206 12% 70%',
			'--input': '206 12% 70%',
			'--ring': '211 30% 32%',
			'--chart-1': '211 30% 32%',
			'--chart-2': '192 38% 40%',
			'--chart-3': '28 50% 48%',
			'--chart-4': '156 22% 38%',
			'--chart-5': '336 30% 46%',
			'--sidebar-background': '205 18% 88%',
			'--sidebar-foreground': '214 22% 18%',
			'--sidebar-primary': '211 30% 32%',
			'--sidebar-primary-foreground': '210 28% 97%',
			'--sidebar-accent': '193 28% 79%',
			'--sidebar-accent-foreground': '214 22% 18%',
			'--sidebar-border': '206 12% 70%',
			'--sidebar-ring': '211 30% 32%',
		},
	},
]);

export const DEFAULT_THEME_ID = 'default-dark';
export const DEFAULT_LIGHT_THEME_ID = 'default-light';
